import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import {
   useSubscribeToNewsletter,
   SubscriptionStatus,
   getNewsletterSubscriptionEvent,
} from '@ifixit/newsletter-sdk';
import { useAppContext } from '@ifixit/app/hooks';
import { useSessionStorageState } from '../hooks/use-session-storage-state';
import {
   Dialog,
   DialogClose,
   DialogContent,
   DialogTitle,
   DialogDescription,
} from '../components/dialog';
import { FaIcon } from '../components/icon';
import { useTranslations } from '@ifixit/i18n/hooks';
import { useTrackFirstView } from '@ifixit/analytics/hooks/use-track-first-view';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

type NewsletterModalProps = {
   open: boolean;
   onClose: () => void;
   storeCode: string;
   privacyPolicyUrl: string;
};

const SESSION_STORAGE_KEY = 'newsletter-modal-seen';

export function NewsletterModal({
   open,
   onClose,
   storeCode,
   privacyPolicyUrl,
}: NewsletterModalProps) {
   const t = useTranslations('NewsletterModal');
   const { isLoggedIn } = useAppContext();
   const growthbook = useGrowthBook();
   const [seenAlready, setSeenAlready] = useSessionStorageState<boolean>(SESSION_STORAGE_KEY);
   const [email, setEmail] = useState('');

   const shownRef = useTrackFirstView<HTMLDivElement>(
      getNewsletterSubscriptionEvent('Modal', 'Shown')
   );

   const [subscription, subscribe] = useSubscribeToNewsletter({
      storeCode,
      analyticsSource: 'Modal',
   });
   const { status, error } = subscription;

   const wrappedOnClose = ({ sendAnalytics }: { sendAnalytics: boolean }) => {
      setSeenAlready(true);
      if (sendAnalytics) {
         trackAnalyticsCustomEvent(getNewsletterSubscriptionEvent('Modal', 'Dismissed'));
      }
      onClose();
   };

   useEffect(() => {
      if (status === SubscriptionStatus.Subscribed) {
         setTimeout(() => {
            wrappedOnClose({ sendAnalytics: false });
         }, 1000);
      }
   }, [status, error]);

   const isSubscribed = status === SubscriptionStatus.Subscribed;
   const loading = status === SubscriptionStatus.Subscribing;

   const isOpen =
      open && !seenAlready && !isLoggedIn && growthbook.isOn('show-answers-news-letter');

   return (
      <Dialog
         open={isOpen}
         onOpenChange={(val: boolean) => {
            if (!val && !loading) {
               wrappedOnClose({ sendAnalytics: true });
            }
         }}
      >
         <DialogContent
            ref={shownRef}
            className="w-full max-w-lg max-h-[calc(100dvh-var(--safe-top)-2rem)] overflow-y-auto rounded-2xl p-8 bg-white shadow-2xl duration-2000"
            onInteractOutside={e => {
               // if interacting with consent banner, don't close the modal
               const consentBanner = document.querySelector('[data-testid="consent-banner"]');
               if (consentBanner && consentBanner.contains(e.target as Node)) {
                  e.preventDefault();
               }
            }}
         >
            <div className="flex justify-center">
               <IFixitLogo className="h-12 w-12" />
            </div>

            <DialogTitle className="flex flex-col gap-1 text-center text-2xl font-semibold text-gray-900">
               {t('title')}
               <DialogDescription className="text-center text-lg font-semibold text-gray-900">
                  {t('subtitle')}
               </DialogDescription>
            </DialogTitle>

            <BulletList />

            {isSubscribed && (
               <div className="flex items-center justify-center mt-4">
                  <p className="text-center text-green-600">{t('successMessage')}</p>
                  <FaIcon
                     name="circle-check"
                     className="ml-2 h-6 w-6 text-green-600 flex-shrink-0"
                  />
               </div>
            )}

            {!isSubscribed && (
               <form
                  onSubmit={(e?: FormEvent) => {
                     e?.preventDefault();
                     trackAnalyticsCustomEvent({
                        eventCategory: 'Newsletter Modal',
                        eventAction: 'Newsletter Modal Submitted',
                     });
                     subscribe(email);
                  }}
               >
                  <input
                     id="nm-email"
                     name="email"
                     type="email"
                     value={email}
                     onChange={e => setEmail(e.target.value)}
                     placeholder={t('emailPlaceholder')}
                     aria-label={t('emailLabel')}
                     aria-required="true"
                     aria-invalid={!!error}
                     aria-describedby={error ? 'nm-email-error' : undefined}
                     autoComplete="email"
                     className="w-full rounded-md border border-gray-200 px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                     disabled={loading}
                     required
                  />
                  {error && (
                     <p id="nm-email-error" className="mt-2 text-sm text-red-600">
                        {error}
                     </p>
                  )}

                  <button
                     type="submit"
                     disabled={loading}
                     className="mt-4 w-full rounded-lg bg-blue-600 py-3 text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                     {loading ? t('subscribeButtonLoading') : t('subscribeButton')}
                  </button>
               </form>
            )}

            <p className="mt-6 text-center text-sm text-gray-600">
               {t.rich('privacy', {
                  link: chunks => (
                     <a
                        href={privacyPolicyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-blue-600"
                     >
                        {chunks}
                     </a>
                  ),
               })}
            </p>

            <DialogClose
               disabled={loading}
               className="mt-2 w-full text-center text-sm text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
               {t('noThanks')}
            </DialogClose>
         </DialogContent>
      </Dialog>
   );
}

function BulletList() {
   const t = useTranslations('NewsletterModal');
   const renderBold = (chunks: ReactNode) => (
      <span className="text-gray-900 font-bold">{chunks}</span>
   );
   const bullets = [
      t.rich('bullet1', {
         bold: renderBold,
      }),
      t.rich('bullet2', {
         bold: renderBold,
      }),
      t.rich('bullet3', {
         bold: renderBold,
      }),
   ];
   return (
      <ul className="space-y-3">
         {bullets.map((bullet, index) => (
            <li key={index} className="flex gap-3 items-center">
               <FaIcon name="circle-check" className="h-6 w-6 text-blue-600 flex-shrink-0" />
               <p className="text-gray-700">{bullet}</p>
            </li>
         ))}
      </ul>
   );
}

function IFixitLogo({ className }: { className?: string }) {
   return (
      <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
         <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M30.6159 25.0102L35.3097 31.2749C35.699 31.7948 35.721 32.6592 35.3586 33.1982L35.1213 33.55C34.76 34.0878 34.0334 34.8358 33.5075 35.2123L32.9431 35.6135C32.4181 35.9891 31.5678 35.9754 31.0552 35.582L24.8559 30.8311C24.3433 30.4376 23.5033 30.4376 22.9904 30.8311L16.7915 35.582C16.2783 35.9754 15.4236 35.9948 14.8948 35.6269L14.4849 35.3427C13.9558 34.9727 13.2189 34.2337 12.8505 33.7006L12.4969 33.1881C12.1286 32.6559 12.1445 31.7948 12.5319 31.2749L17.2322 25.0072C17.6197 24.4869 17.6197 23.6393 17.2322 23.1196L12.5319 16.8502C12.1445 16.3302 12.1298 15.4721 12.501 14.9402L12.9006 14.3696C13.2704 13.8378 14.0103 13.1032 14.5426 12.7383L14.8915 12.4984C15.4236 12.1317 16.2783 12.1558 16.7915 12.5475L22.9904 17.2987C23.5033 17.6919 24.3433 17.6919 24.8559 17.2987L31.0552 12.5475C31.5678 12.1558 32.4402 12.1034 32.9961 12.4344L33.6444 12.8216C34.2 13.1523 34.909 13.8896 35.2214 14.4589L35.4499 14.8759C35.7608 15.4438 35.699 16.3346 35.3097 16.8546L30.6159 23.1226C30.2267 23.6422 30.2267 24.4917 30.6159 25.0102ZM23.9997 -3.73131e-09C10.8876 -3.73131e-09 0.258301 10.7447 0.258301 23.9985C0.258301 37.2538 10.8876 48 23.9997 48C37.1122 48 47.7415 37.2538 47.7415 23.9985C47.7415 10.7447 37.1122 -3.73131e-09 23.9997 -3.73131e-09Z"
            fill="#1975F1"
         />
      </svg>
   );
}
